import config from '../../config'

import {
  DateRange,
  Message,
  ProviderRatingAggregated,
  ProviderReview,
} from '../../../../commons/src/types'
import {ShippingId} from '../../types'

import {
  AdditionalCostId,
  AttachmentId,
  ModelId,
  Order,
  OrderCreditNote,
  OrderCreditNoteV2,
  OrderId,
  OrderInvoice,
  OrderInvoiceV2,
  OrderRefund,
  OrderStatusType,
  PricingVersion,
  RefundCustomAmount,
  RefundQuote,
  RefundType,
} from '../../order-types'
import httpJsonAuth from '../http-json-auth'

type OrdersResponse = Order[]

type OrderMessageResponse = Message

export const getOrders = async (
  creationDateRange: DateRange,
  statusFilters?: OrderStatusType[]
): Promise<OrdersResponse> => {
  const creationDateRangeQuery = `?fromDate=${creationDateRange.from}&toDate=${creationDateRange.to}`
  const statusQuery =
    statusFilters && statusFilters.length > 0 ? `&status=${statusFilters.join(',')}` : ''
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order${creationDateRangeQuery}${statusQuery}`
  )
  return response.json
}

export const getOrder = async (orderId: OrderId): Promise<Order> => {
  const response = await httpJsonAuth.fetch(`${config.services.partner.baseUrl}/order/${orderId}`)
  return response.json
}

const createInvoicesAndCreditNotesQuery = (
  creationDateRange: DateRange,
  pricingVersion: PricingVersion
) => {
  const creationDateRangeQuery = `fromDate=${creationDateRange.from}&toDate=${creationDateRange.to}`
  const pricingVersionQuery = `pricingVersion=${pricingVersion}`
  return `?${creationDateRangeQuery}&${pricingVersionQuery}`
}

export const getInvoices = async (creationDateRange: DateRange): Promise<OrderInvoice[]> => {
  const query = createInvoicesAndCreditNotesQuery(creationDateRange, PricingVersion.V1)
  const response = await httpJsonAuth.fetch(`${config.services.partner.baseUrl}/invoice${query}`)
  return response.json
}

export const getInvoicesV2 = async (creationDateRange: DateRange): Promise<OrderInvoiceV2[]> => {
  const query = createInvoicesAndCreditNotesQuery(creationDateRange, PricingVersion.V2)
  const response = await httpJsonAuth.fetch(`${config.services.partner.baseUrl}/invoice${query}`)
  return response.json
}

export const getCreditNotes = async (creationDateRange: DateRange): Promise<OrderCreditNote[]> => {
  const query = createInvoicesAndCreditNotesQuery(creationDateRange, PricingVersion.V1)
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/credit-note${query}`
  )
  return response.json
}

export const getCreditNotesV2 = async (
  creationDateRange: DateRange
): Promise<OrderCreditNoteV2[]> => {
  const query = createInvoicesAndCreditNotesQuery(creationDateRange, PricingVersion.V2)
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/credit-note${query}`
  )
  return response.json
}

export type PostMessagePayload = {
  text: string
  attachments?: File[]
}
export const postMessage = async (
  orderId: OrderId,
  payload: PostMessagePayload
): Promise<OrderMessageResponse> => {
  const response = await httpJsonAuth.upload({
    method: 'POST',
    url: `${config.services.partner.baseUrl}/order/${orderId}/message`,
    body: payload,
  })
  return response.json
}

export type PostAdditionalCostPayload = {
  net: number
  description: string
}

type AdditionalCostResponse = {
  net: number
  description: string
  id: string
  vendorId: string
  currency: string
}

export const postAdditionalCostRequest = async (
  orderId: OrderId,
  payload: PostAdditionalCostPayload
): Promise<AdditionalCostResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/additionalCost`,
    {
      method: 'POST',
      body: payload,
    }
  )
  return response.json
}

export const patchOrderStatus = async (
  orderId: OrderId,
  payload: {
    status: OrderStatusType
    trackingNumber?: string
  }
): Promise<void> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/status`,
    {
      method: 'PATCH',
      body: payload,
    }
  )
  return response.json
}

export type DownloadResponse = {
  url: string // S3 signed URL
}

export const getModelDownload = async (
  orderId: OrderId,
  modelId: ModelId
): Promise<DownloadResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/model/${modelId}/download`
  )
  return response.json
}

export const getModelDownloadAll = async (orderId: OrderId): Promise<Blob> =>
  httpJsonAuth.fetchBlob(`${config.services.partner.baseUrl}/order/${orderId}/model/downloadAll`)

export const getOrderAttachmentDownload = async (
  orderId: OrderId,
  attachmentId: AttachmentId
): Promise<DownloadResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/attachment/${attachmentId}/download`
  )
  return response.json
}

export const getOrderReview = async (orderId: OrderId): Promise<ProviderReview> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/review/order/${orderId}`
  )
  return response.json
}

export const patchOrderReviewReply = async (
  reviewId: string,
  vendorReply: string
): Promise<void> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/review/${reviewId}/reply`,
    {
      method: 'PATCH',
      body: {
        vendorReply,
      },
    }
  )
  return response.json
}

export const patchOrderMessagesReadStatus = async (orderId: OrderId): Promise<void> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/message/read`,
    {
      method: 'PATCH',
    }
  )
  return response.json
}

export const getAggregatedReview = async (): Promise<ProviderRatingAggregated> => {
  const response = await httpJsonAuth.fetch(`${config.services.partner.baseUrl}/review/aggregate`)
  return response.json
}

export type RefundRequest = FullRefundRequest | PartialRefundRequest | CustomRefundRequest

type FullRefundRequest = {
  refundType: RefundType.FULL
  reason: string
}

type PartialRefundRequest = {
  refundType: RefundType.PARTIAL
  reason: string
  quotes: RefundQuote[]
  shippings: ShippingId[]
  additionalCosts: AdditionalCostId[]
}

type CustomRefundRequest = {
  refundType: RefundType.CUSTOM
  reason: string
  customAmount: RefundCustomAmount
}

export const postRefund = async (
  orderId: OrderId,
  refundRequest: RefundRequest
): Promise<OrderRefund> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/refund`,
    {
      method: 'POST',
      body: refundRequest,
    }
  )
  return response.json
}

export const getModelAttachmentDownload = async (
  orderId: OrderId,
  modelId: ModelId,
  attachmentId: AttachmentId
): Promise<DownloadResponse> => {
  const response = await httpJsonAuth.fetch(
    `${config.services.partner.baseUrl}/order/${orderId}/model/${modelId}/attachment/${attachmentId}/download`
  )
  return response.json
}
